import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { MultiSelect } from 'primereact/multiselect';
import { RadioButton } from 'primereact/radiobutton';
import { Tag } from 'primereact/tag';
import { flagDefinitionsApi } from '../../api/flagSystemApi';
import {
  emptyFlag, FLAG_DAYS, FLAG_ROLE_LABELS, flagAccessSummary, flagError,
  flagFieldErrors, flagRequest, isDuplicateFlagCodeError, validateFlag,
} from '../../utils/flagSystem';
import { Feedback, Field, FlagPage, ResourceState, useRemoteResource } from './FlagSystemCommon';
import FlagCsvImportDialog from './FlagCsvImportDialog';
import { confirmFlagApplicability, FLAG_APPLICABILITY_OPTIONS, flagApplicabilityFields,
  flagApplicabilityLabel, flagApplicabilityValue, hasFlagApplicability } from '../../utils/flagApplicability';

const actionFields = [
  ['juniorAction', 'Junior Local User', 'JuniorLocalUser', 'e.g. Deny entry, contact manager'],
  ['dutyManagerAction', 'Duty Manager', 'DutyManager', 'e.g. Deny entry, escalate if needed'],
  ['seniorAction', 'Senior Local User', 'SeniorLocalUser', 'e.g. Use discretion, may override'],
];
const orderedPermissions = (flag) => [...flag.permissions].sort((a, b) =>
  actionFields.findIndex(field => field[2] === a.role) - actionFields.findIndex(field => field[2] === b.role));
let scheduleSequence = 0;
const scheduleRow = (time = {}) => ({
  ...time, daysOfWeek: Array.isArray(time.daysOfWeek) ? [...time.daysOfWeek] : [],
  start: time.start || '09:00', end: time.end || '17:00', scheduleId: `schedule-${scheduleSequence += 1}`,
});
const editableFlag = (flag) => ({
  ...emptyFlag(), ...flag,
  applyOnEntry: flag.applyOnEntry, applyOnExit: flag.applyOnExit,
  description: flag.description || '', juniorAction: flag.juniorAction || '',
  dutyManagerAction: flag.dutyManagerAction || '', seniorAction: flag.seniorAction || '',
  times: (flag.times || []).map(scheduleRow),
  permissions: flag.permissions.map(permission => ({ ...permission })),
});

const FlagManagement = ({ account, onAccessError }) => {
  const navigate = useNavigate();
  const { customerId } = account;
  const loader = useCallback(() => flagDefinitionsApi.list(customerId), [customerId]);
  const resource = useRemoteResource(loader, onAccessError);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(emptyFlag);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [hideDisabled, setHideDisabled] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const busy = useRef(false);
  const applicabilitySupported = Boolean(editor && editor.applicabilitySupported);
  const flags = resource.data || [];
  const visibleFlags = hideDisabled ? flags.filter(flag => flag.active) : flags;
  const canImportCsv = account.role === 'CustomerLevelUser';

  const open = (flag, duplicate = false) => {
    setEditor({ flagId: flag && !duplicate ? flag.flagId : null, sourceName: flag && flag.name, duplicate,
      applicabilitySupported: flag ? hasFlagApplicability(flag) :
        Boolean(resource.data && resource.data.length && resource.data.every(hasFlagApplicability)) });
    setForm(flag ? { ...editableFlag(flag), ...(duplicate ? {
      name: `${flag.name} (copy)`.slice(0, 200), code: '', revision: 0, active: true,
    } : {}) } : emptyFlag());
    setErrors({}); setError(''); setMessage(''); setConflict(false); setUnverified(false);
  };
  const close = () => { if (!saving) { setEditor(null); setForm(emptyFlag()); setErrors({}); setError(''); setConflict(false); } };
  const clear = () => {
    setForm(previous => ({ ...emptyFlag(), revision: previous.revision, active: previous.active,
      ...(editor.flagId ? { applyOnEntry: previous.applyOnEntry, applyOnExit: previous.applyOnExit } : {}) }));
    setErrors({});
  };
  const change = (field, value) => setForm(previous => ({ ...previous, [field]: value }));
  const handleFailure = (failure, fallback) => {
    onAccessError(failure);
    if (isDuplicateFlagCodeError(failure)) {
      setError('A flag with this code already exists for this customer, including disabled flags. Choose a different code.');
      setErrors(previous => ({ ...previous, code: 'This flag code is already in use for this customer.' }));
      return;
    }
    setError(flagError(failure, fallback));
    setErrors(flagFieldErrors(failure));
    if (failure.response && failure.response.status === 409) setConflict(true);
  };
  const save = async () => {
    if (busy.current || unverified) return;
    const validation = validateFlag(form, { validateApplicability: applicabilitySupported });
    setErrors(validation);
    if (Object.keys(validation).length || conflict) return;
    busy.current = true; setSaving(true); setError('');
    let written = false;
    let savedId = editor.flagId;
    try {
      const request = flagRequest(form, applicabilitySupported);
      const saved = editor.flagId ? await flagDefinitionsApi.update(customerId, editor.flagId, request) :
        await flagDefinitionsApi.create(customerId, request);
      written = true;
      savedId = (saved && saved.flagId) || savedId;
      if (applicabilitySupported) {
        confirmFlagApplicability(saved, request, savedId);
        if (!savedId) throw new Error('The server did not return a flag ID for persistence verification.');
        confirmFlagApplicability(await flagDefinitionsApi.get(customerId, savedId), request, savedId);
      }
      setEditor(null); setForm(emptyFlag());
      setMessage(applicabilitySupported ? 'Flag saved. Applicability confirmed after reload.' : 'Flag saved without applicability editing. Selected applicability was not sent or verified.');
      resource.reload();
    } catch (failure) {
      if (written || failure.code === 'FLAG_APPLICABILITY_UNVERIFIED' ||
          (applicabilitySupported && (!failure.response || failure.response.status >= 500))) {
        setUnverified(true);
        setEditor(previous => ({ ...previous, flagId: savedId || failure.savedFlagId || null }));
        setError(`Applicability integration is unverified. Your selection is preserved; do not repeat the save. ${failure.message || 'Reload failed after the write.'}`);
        onAccessError(failure);
        resource.reload();
      } else handleFailure(failure, 'Unable to save the flag.');
    } finally { busy.current = false; setSaving(false); }
  };
  const disable = async (flag) => {
    setSaving(true); setError(''); setMessage('');
    try {
      await flagDefinitionsApi.disable(customerId, flag.flagId, flag.revision);
      setConfirmation(null); setMessage('Flag disabled. Existing assignments are retained.'); resource.reload();
    } catch (failure) {
      handleFailure(failure, 'Unable to disable the flag.');
      setConfirmation(null);
    } finally { setSaving(false); }
  };
  const reloadEditor = async () => {
    setConfirmation(null); setSaving(true); setError('');
    try {
      const latest = await flagDefinitionsApi.get(customerId, editor.flagId);
      if (unverified) {
        confirmFlagApplicability(latest, form, editor.flagId);
        setForm(editableFlag(latest)); setUnverified(false); setErrors({}); setConflict(false);
        setMessage('Saved applicability confirmed after reload.');
        resource.reload();
        return;
      }
      setForm(editableFlag(latest)); setErrors({}); setConflict(false);
      setEditor(previous => ({ ...previous, applicabilitySupported: hasFlagApplicability(latest) }));
    } catch (failure) { handleFailure(failure, 'Unable to reload the flag.'); }
    finally { setSaving(false); }
  };
  const permissionList = (flag, property) => {
    const permitted = orderedPermissions(flag).filter(permission => permission[property]);
    return permitted.length ? <div className="flag-definition-permissions">
      {permitted.map(permission => <Tag key={permission.role} value={FLAG_ROLE_LABELS[permission.role]} severity="success" />)}
    </div> : <span className="flag-definition-none">{property === 'canOverride' ? 'No overrides permitted' : 'No roles permitted'}</span>;
  };

  return <div className="flag-management-surface"><FlagPage title="Flag Management" className="flag-management-page" containerFluid="md" actions={<>
    <Button label="Audit Log" icon="pi pi-history" className="p-button-text" disabled={saving} onClick={() => navigate('/support/flags/audit-log')} />
    <Button aria-label="Refresh flags" title="Refresh flags" icon="pi pi-refresh" className="p-button-text p-button-rounded" disabled={saving} onClick={() => { setError(''); setConflict(false); resource.reload(); }} />
    {canImportCsv && <FlagCsvImportDialog customerId={customerId} flags={resource.data || []} flagsAvailable={Array.isArray(resource.data)}
      onAccessError={onAccessError} onImported={resource.reload} />}
    <Button label="Create Flag" icon="pi pi-plus" className="p-button-success" onClick={() => open(null)} disabled={saving || resource.loading || Boolean(resource.error)} />
  </>}>
    <Feedback error={!editor ? error : ''} message={message} />
    {resource.data && (!resource.data.length || resource.data.some(flag => !hasFlagApplicability(flag))) &&
      <Message severity="warn" text="BACKEND NOT YET AVAILABLE: flag definition responses do not verify applyOnEntry/applyOnExit. Applicability editing is unavailable for those records. Existing configuration editing remains available without sending unsupported fields." />}
    {!editor && conflict && <Message severity="warn" text="The flag changed. Refresh the list before attempting another change." />}
    <ResourceState resource={resource}>
      <div className="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 className="m-0">{hideDisabled ? 'Enabled Flags' : 'All Flags'}</h5>
        <div className="flex align-items-center flex-wrap gap-3">
          <div className="flex align-items-center gap-2">
            <input id="hide-disabled-flags" className="flag-status-filter" type="checkbox" checked={hideDisabled}
              onChange={event => setHideDisabled(event.target.checked)} />
            <label htmlFor="hide-disabled-flags">Hide disabled flags</label>
          </div>
          <Tag value={hideDisabled ? `${visibleFlags.length} of ${flags.length} flags` :
            `${flags.length} ${flags.length === 1 ? 'flag' : 'flags'}`} />
        </div>
      </div>
      <DataTable className="flag-definitions-table" value={visibleFlags} dataKey="flagId"
        emptyMessage={<div className="flag-definition-empty">
          <i className="pi pi-flag" aria-hidden="true" />
          <strong>{hideDisabled && flags.length ? 'No enabled flags' : 'No flags created yet'}</strong>
          {hideDisabled && flags.length ? 'Uncheck "Hide disabled flags" to view disabled flags.' :
            <>Use the &quot;Create Flag&quot; button above to add your first access-control flag.</>}
        </div>} responsiveLayout="stack" breakpoint="860px">
        <Column field="name" header="Flag Name" body={flag => <div>
          <span className="flag-definition-name">{flag.name}</span>
          {!flag.active && <div className="mt-2"><Tag value="Disabled" severity="warning" /></div>}
        </div>} />
        <Column field="code" header="Flag Code" body={flag => <span className="flag-definition-code">{flag.code}</span>} />
        <Column field="description" header="Description" body={flag => flag.description ?
          <span className="flag-definition-description">{flag.description}</span> : <span className="flag-definition-none">No description</span>} />
        <Column header="Applies to" body={flag => <Tag value={flagApplicabilityLabel(flag)}
          severity={!flagApplicabilityValue(flag) ? 'warning' : 'info'} />} />
        <Column header="Access Rule" body={flag => <>
          <Tag value={flag.blockAllTime ? 'Block all time' : 'Scheduled'} severity={flag.blockAllTime ? 'danger' : 'info'} />
          {!flag.blockAllTime && <div className="flag-definition-schedule-summary">{flagAccessSummary(flag)}</div>}
        </>} />
        <Column header="Override Permissions" body={flag => permissionList(flag, 'canOverride')} />
        <Column header="Add/Remove Permissions" body={flag => permissionList(flag, 'canAddRemove')} />
        <Column header="Actions" body={flag => <div className="flag-definition-actions">
          <Button aria-label={`Edit ${flag.name}`} title="Edit flag" icon="pi pi-pencil" className="p-button-text p-button-rounded" disabled={saving} onClick={() => open(flag)} />
          <Button aria-label={`Duplicate ${flag.name}`} title="Duplicate flag" icon="pi pi-copy" className="p-button-text p-button-rounded" disabled={saving} onClick={() => open(flag, true)} />
          <Button aria-label={`${flag.active ? 'Disable' : 'Re-enable'} ${flag.name}`} title={flag.active ? 'Disable flag' : 'Re-enable flag'} icon={flag.active ? 'pi pi-ban' : 'pi pi-check'}
            className={`p-button-text p-button-rounded ${flag.active ? 'p-button-danger' : 'p-button-success'}`} disabled={saving || conflict}
            onClick={() => flag.active ? setConfirmation({ kind: 'disable', flag }) : setConfirmation({ kind: 'enable', flag })} />
        </div>} />
      </DataTable>
    </ResourceState>

    <Dialog header={editor && editor.flagId ? 'Edit Flag' : editor && editor.duplicate ? 'Duplicate Flag' : 'Create a Flag'} visible={Boolean(editor)}
      className="flag-editor flag-definition-editor" position="top" draggable={false} resizable={false} modal closable={!saving} onHide={close}
      footer={<div className="flex justify-content-center flex-wrap gap-3">
        <Button label="Clear Form" icon="pi pi-times" className="p-button-warning" onClick={clear} disabled={saving || unverified} />
        <Button label="Save Flag" icon="pi pi-check" className="p-button-success" onClick={save} loading={saving} disabled={saving || conflict || unverified} />
      </div>}>
      {editor && editor.sourceName && <div className={`flag-definition-banner ${editor.duplicate ? 'flag-definition-banner-copy' : ''}`}>
        <span><i className={editor.duplicate ? 'pi pi-copy mr-2' : 'pi pi-pencil mr-2'} aria-hidden="true" />
          {editor.duplicate ? 'Duplicating flag: ' : 'Editing flag: '}{editor.sourceName}</span>
        <Button label={editor.duplicate ? 'Cancel duplicate' : 'Cancel editing'} className="p-button-link p-button-sm" onClick={close} disabled={saving} />
      </div>}
      <Feedback error={error} />
      {unverified && <div className="mb-3"><Message severity="warn" text="The write may already have succeeded, but applicability persistence is unverified. Your selection has not been discarded. No automatic retry will be made." />
        {editor.flagId && <Button label="Verify saved applicability" className="p-button-text" disabled={saving} onClick={reloadEditor} />}</div>}
      {conflict && <div className="mb-3"><Message severity="warn" text="The configuration changed. Your edits are preserved. Copy or reconcile them before loading the latest revision." />
        <Button label="Reload latest" className="p-button-text" disabled={saving} onClick={() => setConfirmation({ kind: 'reload' })} /></div>}
      <fieldset disabled={unverified} className="border-0 p-0">
      <h5 className="flag-definition-section flag-definition-section-first">Flag Details</h5>
      <Field id="flag-name" label={<>Flag Name <span className="flag-definition-optional">(required)</span></>} error={errors.name}>
        <InputText id="flag-name" value={form.name} placeholder="e.g. Suspended Member" maxLength={200} disabled={saving}
          className={errors.name ? 'p-invalid' : ''} aria-required="true" aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? 'flag-name-error' : undefined} onChange={event => change('name', event.target.value)} />
      </Field>
      <Field id="flag-code" label={<>Flag Code <span className="flag-definition-optional">(required)</span></>} error={errors.code}>
        <InputText id="flag-code" value={form.code} placeholder="e.g. OFF-PEAK" maxLength={64} disabled={saving}
          className={errors.code ? 'p-invalid' : ''} aria-required="true" aria-invalid={Boolean(errors.code)}
          aria-describedby={errors.code ? 'flag-code-error' : undefined}
          onChange={event => change('code', event.target.value.toUpperCase())} />
      </Field>
      <Field id="flag-description" label={<>Flag Description <span className="flag-definition-optional">(optional)</span></>} error={errors.description}>
        <InputTextarea id="flag-description" value={form.description} placeholder="Describe when and why this flag is used..."
          maxLength={2000} rows={3} disabled={saving} onChange={event => change('description', event.target.value)} />
      </Field>
      <section aria-labelledby="flag-applicability-heading">
        <h5 id="flag-applicability-heading" className="flag-definition-section">Applies to</h5>
        <p className="flag-definition-hint">Choose which access-point directions this flag affects.</p>
        {!applicabilitySupported && <Message severity="warn" text="BACKEND NOT YET AVAILABLE: applyOnEntry/applyOnExit must be exposed by the definition request/response DTOs. No selected applicability will be sent or claimed as saved." />}
        {hasFlagApplicability(form) && !flagApplicabilityValue(form) && <Message severity="warn" text="Configuration warning: neither direction is selected. Choose a valid applicability before saving." />}
        <div role="radiogroup" aria-labelledby="flag-applicability-heading">
          {FLAG_APPLICABILITY_OPTIONS.map(option => <div key={option.value} className={`flag-definition-radio-option ${flagApplicabilityValue(form) === option.value ? 'is-selected' : ''}`}>
            <input type="radio" id={`flag-scope-${option.value}`} className="flag-applicability-radio" name="flagApplicability"
              value={option.value} checked={flagApplicabilityValue(form) === option.value}
              disabled={saving || unverified || !applicabilitySupported}
              onChange={() => setForm(previous => ({ ...previous, ...flagApplicabilityFields(option.value) }))} />
            <label htmlFor={`flag-scope-${option.value}`}>{option.value === 'both' ? 'Entry and exit' : option.label}</label>
          </div>)}
        </div>
        {errors.applicability && <Message severity="error" text={errors.applicability} />}
      </section>
      <h5 className="flag-definition-section">Action Wording</h5>
      <p className="flag-definition-hint">What should be shown / said to staff at the access point for this flag, per user level. Wording is optional.</p>
      <div className="flag-definition-role-grid mb-3">
        {actionFields.map(([field, label, , placeholder]) => <Field key={field} id={field} label={label} error={errors[field]}>
          <InputText id={field} value={form[field]} placeholder={placeholder} maxLength={2000} disabled={saving} onChange={event => change(field, event.target.value)} />
        </Field>)}
      </div>
      <h5 className="flag-definition-section">Access Rule</h5>
      <p className="flag-definition-hint">These shared access rules apply only within the selected direction(s).</p>
      {[{ block: true, id: 'block-all', title: 'Block access all the time', description: 'There are no allowed-access windows for the selected direction(s).' },
        { block: false, id: 'allow-schedule', title: 'Allow access at the configured days and times', description: 'Configure the days and times windows when you want to allow access for this flag.' }].map(option =>
        <div key={option.id} className={`flag-definition-radio-option ${form.blockAllTime === option.block ? 'is-selected' : ''}`}>
          <RadioButton inputId={option.id} name="accessMode" checked={form.blockAllTime === option.block} disabled={saving}
            onChange={() => { if (form.blockAllTime !== option.block) setForm(previous => ({ ...previous, blockAllTime: option.block, times: [] })); }} />
          <label htmlFor={option.id}><span className="flag-definition-option-title">{option.title}</span>
            <span className="flag-definition-option-description">{option.description}</span></label>
        </div>)}
      <div className={`flag-definition-schedule ${form.blockAllTime ? 'is-disabled' : ''}`}>
        {form.times.map((time, index) => <div key={time.scheduleId} className="flag-definition-day">
          <div className="flag-definition-day-row">
            <Field id={`schedule-days-${time.scheduleId}`} label="Weekdays" error={errors[`daysOfWeek${index}`]}>
              <MultiSelect inputId={`schedule-days-${time.scheduleId}`} value={time.daysOfWeek} options={FLAG_DAYS}
                optionLabel="label" optionValue="value" display="chip" placeholder="Select weekdays"
                disabled={saving || form.blockAllTime} aria-label={`Schedule ${index + 1} weekdays`}
                onChange={event => change('times', form.times.map(item => item.scheduleId === time.scheduleId ?
                  { ...item, daysOfWeek: event.value || [] } : item))} />
              <div className="flag-definition-selected-days" aria-live="polite">
                {time.daysOfWeek.length ? time.daysOfWeek.map(day => FLAG_DAYS.find(option => option.value === day).label).join(', ') :
                  'No weekdays selected'}
              </div>
            </Field>
            {['start', 'end'].map(property => <Field key={property} id={`${property}-${time.scheduleId}`} label={property === 'start' ? 'Start' : 'End'}>
              <InputText id={`${property}-${time.scheduleId}`} value={time[property]} placeholder="HH:mm" maxLength={5} inputMode="numeric"
                disabled={saving || form.blockAllTime} className={errors[`time${index}`] ? 'p-invalid' : ''}
                aria-invalid={Boolean(errors[`time${index}`])} aria-label={`Schedule ${index + 1} ${property}`}
                onChange={event => change('times', form.times.map(item => item.scheduleId === time.scheduleId ?
                  { ...item, [property]: event.target.value } : item))} />
            </Field>)}
            <div className="flag-definition-schedule-action">
              <span>Action</span>
              <Button label="Duplicate schedule" icon="pi pi-copy" className="p-button-text p-button-sm"
                disabled={saving || form.blockAllTime} onClick={() => change('times', [...form.times, scheduleRow(time)])} />
              <Button label="Remove schedule" icon="pi pi-trash" className="p-button-text p-button-danger p-button-sm"
                disabled={saving || form.blockAllTime} onClick={() => change('times', form.times.filter(item => item.scheduleId !== time.scheduleId))} />
            </div>
          </div>
          {errors[`time${index}`] && <small className="flag-error" role="alert">{errors[`time${index}`]}</small>}
        </div>)}
        <Button label="Add schedule" icon="pi pi-plus" className="p-button-text" disabled={saving || form.blockAllTime}
          onClick={() => change('times', [...form.times, scheduleRow()])} />
      </div>
      {errors.times && <Message severity="error" text={errors.times} />}
      {[{ property: 'canOverride', title: 'Override Permission', hint: 'Which user levels are allowed to override this flag at the point of access.', label: 'Can override this flag' },
        { property: 'canAddRemove', title: 'Add / Remove Permission', hint: "Which user levels are allowed to add or remove this flag from a member's profile.", label: 'Can add / remove flag' }].map(section =>
        <section key={section.property} aria-labelledby={`${section.property}-heading`}>
          <h5 id={`${section.property}-heading`} className="flag-definition-section">{section.title}</h5>
          <p className="flag-definition-hint">{section.hint}</p>
          <div className="flag-definition-role-grid">
            {orderedPermissions(form).map(permission => <div key={permission.role} className="flag-definition-role-card">
              <div className="flag-definition-role-name">{FLAG_ROLE_LABELS[permission.role]}</div>
              <div className="flag-definition-permission-check">
                <Checkbox inputId={`${permission.role}-${section.property}`} checked={permission[section.property]} disabled={saving}
                  aria-label={`${FLAG_ROLE_LABELS[permission.role]} ${section.property === 'canOverride' ? 'override' : 'add/remove'}`}
                  onChange={event => change('permissions', form.permissions.map(item => item.role === permission.role ? { ...item, [section.property]: event.checked } : item))} />
                <label htmlFor={`${permission.role}-${section.property}`}>{section.label}</label>
              </div>
            </div>)}
          </div>
        </section>)}
      {errors.permissions && <Message severity="error" text={errors.permissions} />}
      <p className="flag-note mt-3">Override and add/remove permissions are independent. Duplicate names are accepted by the current backend.</p>
      </fieldset>
    </Dialog>
    <Dialog header={confirmation && confirmation.kind === 'reload' ? 'Discard edits and reload?' : 'Change flag status'}
      visible={Boolean(confirmation)} onHide={() => !saving && setConfirmation(null)} closable={!saving} modal
      footer={<><Button label="Cancel" className="p-button-text" disabled={saving} onClick={() => setConfirmation(null)} />
        <Button label="Confirm" disabled={saving} loading={saving} onClick={() => {
          if (confirmation.kind === 'reload') reloadEditor();
          else if (confirmation.kind === 'disable') disable(confirmation.flag);
          else { open({ ...confirmation.flag, active: true }); setConfirmation(null); }
        }} /></>}>
      {confirmation && confirmation.kind === 'reload' ? 'Loading the latest revision will discard your unsaved changes. Nothing will be overwritten on the server.' :
        'Disabling retains existing member assignments. Re-enabling may cause those assignments to apply again. Re-enable opens the editor; Save Flag confirms the server change.'}
    </Dialog>
  </FlagPage></div>;
};
export default FlagManagement;
